// ── EventJourneySection ✦ — recent events, badges, ref excerpts ─
//
// Constitutional rules:
//   • Reference excerpts are TAG-CHIP ONLY — never free-text
//     quotes (Craig answer 2 + Guardrail §13).
//   • No XP bar, no level-up animation, no comparison framing.
//   • Empty states are growth-framed:
//     - events:     "Their EventDrop journey is just getting started ✦"
//     - references: "Trust moments will appear here over time ✦"
import React from 'react';
import {
  PANEL_BORDER, TEXT, TEXT_MUTED, BLUE, VIOLET, ROSE,
} from './palette';
import { Section, CalmPlaceholder } from './Section';

const SubHead = ({ children, top = 4 }) => (
  <p style={{
    margin: `${top}px 0 8px`, fontSize: 10.5, fontWeight: 700,
    letterSpacing: '0.22em', textTransform: 'uppercase', color: TEXT_MUTED,
  }}>
    {children}
  </p>
);

export const EventJourneySection = ({ firstName, journey }) => (
  <Section
    eyebrow="✦ Journey"
    title="Their EventDrop journey so far"
    testid="parent-dashboard-journey"
    accent={VIOLET}
  >
    {!journey.enabled && (
      <CalmPlaceholder testid="parent-dashboard-journey-off">
        {firstName} hasn't shared their journey here yet ✦
      </CalmPlaceholder>
    )}
    {journey.enabled && (
      <>
        <SubHead>Recent events</SubHead>
        {(journey.completed || []).length === 0 && (
          <CalmPlaceholder testid="parent-dashboard-journey-empty">
            Their EventDrop journey is just getting started ✦
          </CalmPlaceholder>
        )}
        {(journey.completed || []).map((c, i) => (
          <div
            key={i}
            data-testid={`parent-dashboard-journey-event-${i}`}
            style={{
              padding: '10px 12px', borderRadius: 12, marginBottom: 8,
              background: 'rgba(255,255,255,0.025)',
              border: `1px solid ${PANEL_BORDER}`,
              display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center',
            }}
          >
            <div style={{ minWidth: 0 }}>
              <p style={{
                margin: 0, fontWeight: 700, fontSize: 13.5, color: TEXT,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {c.title}
              </p>
              <p style={{ margin: '2px 0 0', fontSize: 12, color: TEXT_MUTED }}>
                {c.supplier_name}
              </p>
            </div>
            <span style={{
              flexShrink: 0, fontSize: 11.5, color: TEXT_MUTED, fontWeight: 600,
            }}>
              {c.event_date}
            </span>
          </div>
        ))}

        {(journey.badges || []).length > 0 && (
          <>
            <SubHead top={18}>Badges unlocked</SubHead>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {journey.badges.map((b) => (
                <span
                  key={b.id}
                  data-testid={`parent-dashboard-badge-${b.id}`}
                  title={b.blurb}
                  style={{
                    padding: '6px 12px', borderRadius: 999,
                    background: `linear-gradient(135deg, ${VIOLET}1F, ${BLUE}14)`,
                    border: `1px solid ${VIOLET}3A`,
                    fontSize: 12, fontWeight: 700, color: TEXT,
                  }}
                >
                  ✦ {b.label}
                </span>
              ))}
            </div>
          </>
        )}

        <SubHead top={20}>Trust moments</SubHead>
        {(journey.reference_excerpts || []).length === 0 && (
          <CalmPlaceholder testid="parent-dashboard-references-empty">
            Trust moments will appear here over time ✦
          </CalmPlaceholder>
        )}
        {(journey.reference_excerpts || []).length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {journey.reference_excerpts.map((ex, i) => (
              <span
                key={i}
                data-testid={`parent-dashboard-reference-chip-${i}`}
                style={{
                  padding: '6px 12px', borderRadius: 999,
                  background: `${ROSE}10`, border: `1px solid ${ROSE}3A`,
                  fontSize: 12.5, fontWeight: 700, color: TEXT,
                }}
              >
                {ex.chip} ✦
              </span>
            ))}
            {journey.references_count > (journey.reference_excerpts || []).length && (
              <span
                data-testid="parent-dashboard-references-count"
                style={{
                  padding: '6px 12px', borderRadius: 999,
                  background: 'rgba(255,255,255,0.04)',
                  border: `1px solid ${PANEL_BORDER}`,
                  fontSize: 12, fontWeight: 700, color: TEXT_MUTED,
                }}
              >
                {journey.references_count} references in total
              </span>
            )}
          </div>
        )}
      </>
    )}
  </Section>
);
